import Table from 'shared/components/Table'
import Popup from 'shared/components/Popup'
import { fullDateTimeFormat, relativeDateTimeFormat } from 'libs/date-format'
import { Loader } from 'shared/components/Loader'
import React from 'react'
import { Dropdown } from 'semantic-ui-react'
import * as R from 'ramda'
import usePermission from 'hooks/usePermission'
import { TooltipLabelWithoutIcon } from 'components/TooltipLabelWithoutIcon'
import { useNavbarMenuContext } from 'organisms/NavBar/context'
import constants from 'config/constants'
import './styles.css'

const ArchivedCustomDashboardModal = ({
  Modal,
  archivedItems,
  onRestore,
  onDelete,
  onClose,
  isLoading,
}) => {
  const { user } = usePermission()
  const context = useNavbarMenuContext()
  const contextData = R.prop('contextData', context)
  const featureLimit = R.path(
    ['subscription', 'subscription_items_metadata', 'custom_dashboards'],
    user
  )
  if (isLoading) {
    return (
      <Modal>
        <Modal.Header>Archived dashboards</Modal.Header>
        <Modal.Content>
          <Loader withContainer />
        </Modal.Content>
      </Modal>
    )
  }

  return (
    <Modal>
      <Modal.Header>Archived dashboards</Modal.Header>
      <Modal.Content>
        <p>Archived dashboards are no longer visible to users.</p>
        <ArchivedCustomDashboardTable
          dataSource={R.pathOr(null, ['results'], archivedItems)}
          onRestore={onRestore}
          onDelete={onDelete}
          disableRestoreButton={
            featureLimit
              ? featureLimit <= R.pathOr([], ['customDashboards', 'results'], contextData).length
              : false
          }
        />
      </Modal.Content>
      <Modal.Actions>
        <button className="ui button" onClick={onClose}>
          Close
        </button>
      </Modal.Actions>
    </Modal>
  )
}

export default ArchivedCustomDashboardModal

function ArchivedCustomDashboardTable({ dataSource, onRestore, onDelete, disableRestoreButton }) {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 120,
      render(_, data) {
        return (
          <div className="flex items-center">
            <Popup basic tooltip={<div>{data.name}</div>} size="mini" inverted>
              <div className="truncate">{data.name}</div>
            </Popup>
          </div>
        )
      },
      sorter(dataA, dataB) {
        const labelA = dataA.name
        const labelB = dataB.name
        return (labelA || '').localeCompare(labelB)
      },
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
    },

    {
      title: 'Created',
      key: 'created',
      dataIndex: 'date',
      align: 'left',
      width: 70,
      render(_, data) {
        return renderTimestampField(data.created)
      },
      // @ts-ignore
      sorter: (a, b) => new Date(a.created) - new Date(b.created),
    },
    {
      title: 'Modified',
      key: 'modified',
      align: 'left',
      width: 70,
      render(_, data) {
        return renderTimestampField(data.modified)
      },
      // @ts-ignore
      sorter: (a, b) => new Date(a.modified) - new Date(b.modified),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 25,
      render(_, data) {
        return (
          <React.Fragment>
            <ArchivedDashboardButton
              onDelete={onDelete}
              onRestore={onRestore}
              token={data.token}
              disableRestoreButton={disableRestoreButton}
            />
          </React.Fragment>
        )
      },
    },
  ]

  return (
    <Table
      variant="indexPage"
      clickable={false}
      pagination={false}
      dataSource={dataSource}
      columns={columns}
    />
  )
}

function renderTimestampField(timestamp) {
  if (!timestamp || timestamp === null) {
    return '-'
  }
  return (
    <Popup basic tooltip={fullDateTimeFormat(timestamp)} size="mini" inverted>
      <span>{relativeDateTimeFormat(timestamp)}</span>
    </Popup>
  )
}

const ArchivedDashboardButton = ({ onRestore, onDelete, token, disableRestoreButton }) => {
  const { hasPermission } = usePermission()
  const hasManageCustomDashboardsPermission = hasPermission(
    constants.permissions.manageCustomDashboards
  )

  if (hasManageCustomDashboardsPermission) {
    return (
      <React.Fragment>
        <Dropdown
          icon="far fa-ellipsis-vertical"
          floating
          pointing="top right"
          className="table-elipsis-toolbar-dropdown"
        >
          <Dropdown.Menu>
            <RestoreOption
              onRestore={onRestore}
              token={token}
              disableRestoreButton={disableRestoreButton}
            />
            <Dropdown.Item
              className="!text-negative"
              text="Delete"
              icon="far fa-trash-can"
              onClick={() => onDelete(token)}
            />
          </Dropdown.Menu>
        </Dropdown>
      </React.Fragment>
    )
  } else {
    return null
  }
}

const RestoreOption = ({ onRestore, token, disableRestoreButton }) => {
  if (disableRestoreButton) {
    return (
      <TooltipLabelWithoutIcon
        descriptionTitle={'Dashboard limit reached'}
        descriptionContent={
          "You've reached your account's dashboard limit. Please archive dashboards or upgrade your plan to add more."
        }
        learnMoreLink={'https://help.simplesat.io/en/articles/8856031-custom-dashboards'}
        showGotoBillingPage={true}
        trigger={
          <Dropdown.Item
            className="ss-archived-dashboard-restore-disabled"
            text="Restore"
            icon="far fa-trash-restore"
            onClick={() => {}}
          />
        }
      />
    )
  } else {
    return (
      <Dropdown.Item text="Restore" icon="far fa-trash-restore" onClick={() => onRestore(token)} />
    )
  }
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/ArchivedCustomDashboardModel/index.js